import React, { useState, useEffect } from 'react';
import ChatInterface from './components/ChatInterface';
import ThreadSidebar from './components/ThreadSidebar';
import { chatService } from './services/agentService';
import { sessionService } from './services/sessionService';
import './App.css';

// Generate a 32-character hex UUID (suitable for Bedrock)
function generateSessionId() {
  // Use crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    // Convert UUID (36 chars with hyphens) to 32 hex chars
    return crypto.randomUUID().replace(/-/g, '');
  }
  // Fallback for older browsers
  return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function App() {
  const [currentSession, setCurrentSession] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    try {
      // Load threads first to see if there are any existing threads
      await loadThreads();
      
      // Check for existing session in localStorage
      let sessionId = sessionService.getCurrentSession();
      
      // If no current session, try to use the most recent thread's session ID
      if (!sessionId || !/^[0-9a-f]{32}$/i.test(sessionId)) {
        const threads = sessionService.getSessionsList();
        if (threads && threads.length > 0) {
          // Use the most recently updated thread's session ID
          sessionId = threads[0].id || threads[0].session_id;
          console.log('Using most recent thread session ID:', sessionId);
        } else {
          // Generate a new valid 32-char hex UUID
          sessionId = generateSessionId();
          console.log('Generated new session ID:', sessionId);
        }
        sessionService.setCurrentSession(sessionId);
      } else {
        console.log('Using existing current session ID:', sessionId);
        // Ensure this session ID exists in threads, if not add it
        const threads = sessionService.getSessionsList();
        const sessionExists = threads.some(t => (t.id === sessionId || t.session_id === sessionId));
        if (!sessionExists && sessionService.getMessages(sessionId).length > 0) {
          // Session has messages but no thread entry, create thread entry
          sessionService.addSessionToList({ session_id: sessionId, messages: sessionService.getMessages(sessionId) });
          await loadThreads();
        }
      }
      
      setCurrentSession(sessionId);
    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadThreads = async () => {
    try {
      // Only use localStorage threads (do not merge with backend)
      const localThreads = sessionService.getSessionsList();
      
      // Filter out deleted sessions to prevent bogus entries
      const deletedSessions = sessionService.getDeletedSessions();
      const filteredThreads = localThreads.filter(thread => 
        !deletedSessions.includes(thread.id) && !deletedSessions.includes(thread.session_id)
      );
      
      setThreads(filteredThreads);
    } catch (error) {
      console.error('Error loading threads:', error);
      // Keep existing threads on error
    }
  };

  const handleCreateNew = () => {
    // Generate a new 32-character hex UUID for the new chat
    const newSessionId = generateSessionId();
    console.log('Creating new chat with session ID:', newSessionId);
    
    // Update session storage and state (this will trigger ChatInterface to clear messages)
    sessionService.setCurrentSession(newSessionId);
    setCurrentSession(newSessionId);
    
    // Add to threads
    const newThread = {
      id: newSessionId,
      session_id: newSessionId,  // Explicitly store session_id
      title: 'New Chat',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setThreads([newThread, ...threads]);
    // Persist in localStorage list
    sessionService.addSessionToList({ session_id: newSessionId, messages: [] });
    
    console.log('New chat created, messages should be cleared in ChatInterface');
  };

  const handleSelectSession = (sessionId) => {
    sessionService.setCurrentSession(sessionId);
    setCurrentSession(sessionId);
  };
  
  const handleSessionIdUpdate = (newSessionId) => {
    if (newSessionId && newSessionId !== currentSession) {
      sessionService.setCurrentSession(newSessionId);
      setCurrentSession(newSessionId);
    }
  };

  const handleNewMessage = () => {
    // Reload threads from localStorage to get updated titles and timestamps
    loadThreads();
  };

  const handleDeleteSession = (sessionId) => {
    // Delete from localStorage
    const remainingSessions = sessionService.deleteSession(sessionId);
    
    // If we deleted the current session, switch to another session or create a new one
    if (sessionId === currentSession) {
      if (remainingSessions && remainingSessions.length > 0) {
        // Switch to the most recent remaining session
        const mostRecentSession = remainingSessions[0];
        const switchToId = mostRecentSession.id || mostRecentSession.session_id;
        sessionService.setCurrentSession(switchToId);
        setCurrentSession(switchToId);
      } else {
        // No remaining sessions, create a new one
        const newSessionId = generateSessionId();
        sessionService.setCurrentSession(newSessionId);
        setCurrentSession(newSessionId);
        
        // Persist in localStorage first (this will add it to the sessions list)
        sessionService.addSessionToList({ session_id: newSessionId, messages: [], title: 'New Chat' });
      }
      
      // Reload threads from localStorage to get the updated list
      loadThreads();
    } else {
      // Just update threads list - deleted session is already removed from localStorage
      setThreads(remainingSessions);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="app">
      <ThreadSidebar
        threads={threads}
        currentSession={currentSession}
        onSelectSession={handleSelectSession}
        onCreateNew={handleCreateNew}
        onDeleteSession={handleDeleteSession}
      />
      <div className="main-content">
        <ChatInterface
          sessionId={currentSession}
          onNewMessage={handleNewMessage}
          onSessionIdUpdate={handleSessionIdUpdate}
        />
      </div>
    </div>
  );
}

export default App;

